// 事件驱动动画+音效：监听 state.log 增量，映射到 fxStore + sfx
import { useEffect, useRef } from "react";
import { useGameStore } from "@/store/gameStore";
import { useFxStore, nextFxKey } from "@/store/fxStore";
import { playSfx, type SfxName } from "@/audio/sfx";
import type { GameEvent } from "@/types/game";

const FX_DURATION = 700; // ms
const CAST_DURATION = 500; // ms 发起者特效短一点

interface FxMapping {
  playerId: string;
  cls: string;
  sfx?: SfxName;
  dmg?: number;
  duration?: number; // 自定义持续时间
}

function mapEvent(ev: GameEvent): FxMapping[] {
  switch (ev.type) {
    case "damage":
      return [{ playerId: ev.targetId, cls: "fx--damage", sfx: "damage", dmg: -ev.amount }];
    case "heal":
      return [{ playerId: ev.targetId, cls: "fx--heal", sfx: "heal", dmg: ev.amount }];
    case "play": {
      // 发起者（出牌特效）+ 目标（被指定）
      const out: FxMapping[] = [
        { playerId: ev.playerId, cls: "fx--cast", sfx: "play", duration: CAST_DURATION },
      ];
      if (ev.targetId) {
        const t = Array.isArray(ev.targetId) ? ev.targetId : [ev.targetId];
        for (const tid of t) {
          out.push({ playerId: tid, cls: "fx--target", duration: CAST_DURATION });
        }
      }
      return out;
    }
    case "skill": {
      const out: FxMapping[] = [
        { playerId: ev.playerId, cls: "fx--skill-cast", sfx: "play", duration: CAST_DURATION },
      ];
      if (ev.targetId) {
        const t = Array.isArray(ev.targetId) ? ev.targetId : [ev.targetId];
        for (const tid of t) {
          out.push({ playerId: tid, cls: "fx--target", duration: CAST_DURATION });
        }
      }
      return out;
    }
    case "crit":
      // 暴击：目标金色爆炸 + 伤害源额外脉冲（发起者 fx--crit-source）
      return [
        { playerId: ev.targetId, cls: "fx--crit", sfx: "damage", duration: 1000 },
        { playerId: ev.sourceId, cls: "fx--crit-source", duration: CAST_DURATION },
      ];
    case "judgment":
      // 判定：发起者紫色光圈 + 专用音效；成功时额外播放成功音
      return [
        {
          playerId: ev.playerId,
          cls: ev.success ? "fx--judgment fx--judgment-ok" : "fx--judgment fx--judgment-fail",
          sfx: ev.success ? "judgment-success" : "judgment",
          duration: 1000,
        },
      ];
    case "response":
      switch (ev.kind) {
        case "闪避":
          return [{ playerId: ev.playerId, cls: "fx--dodge", sfx: "dodge" }];
        case "虚灵":
          return [{ playerId: ev.playerId, cls: "fx--void", sfx: "void" }];
        case "反射":
          return [{ playerId: ev.playerId, cls: "fx--reflect", sfx: "reflect" }];
        case "无敌":
          return [{ playerId: ev.playerId, cls: "fx--reflect" }];
        default:
          return [{ playerId: ev.playerId, cls: "fx--card-play", sfx: "play" }];
      }
    case "dying":
      return [{ playerId: ev.playerId, cls: "fx--dying" }];
    case "death":
      return [{ playerId: ev.playerId, cls: "fx--death", sfx: "death" }];
    case "turn-start":
      return [{ playerId: ev.playerId, cls: "fx--turn", sfx: "turn" }];
    case "rescue":
      return [{ playerId: ev.playerId, cls: "fx--heal", sfx: "heal" }];
    case "revive":
      return [{ playerId: ev.playerId, cls: "fx--heal", sfx: "heal" }];
    default:
      return [];
  }
}

// 无 UI 的 watcher hook：Board 顶层挂载一次
export function useEventFx(): void {
  const state = useGameStore((s) => s.state);
  const lastLenRef = useRef(0);
  const setFx = useFxStore((s) => s.setFx);
  const clearFx = useFxStore((s) => s.clearFx);
  const setJudgment = useFxStore((s) => s.setJudgment);
  const clearJudgment = useFxStore((s) => s.clearJudgment);

  useEffect(() => {
    if (!state) {
      lastLenRef.current = 0;
      return;
    }
    const log = state.log;
    const prev = lastLenRef.current;
    lastLenRef.current = log.length;
    if (prev >= log.length) return;

    const names = new Map(state.players.map((p) => [p.id, p.hero.name]));

    // 处理新增事件
    for (let i = prev; i < log.length; i++) {
      const ev = log[i];

      // 判定翻牌覆盖层：设置 judgment fx，2.5s 后自动清除
      if (ev.type === "judgment") {
        setJudgment({
          key: nextFxKey(),
          playerName: names.get(ev.playerId) ?? ev.playerId,
          cardName: ev.cardName,
          suit: ev.result,
          armorName: ev.armorName,
          success: ev.success,
          responseKind: ev.responseKind,
        });
        if (ev.success) playSfx("judgment-success");
        else playSfx("judgment");
        setTimeout(() => clearJudgment(), 2600);
        continue; // 判定覆盖层自带的紫色光圈替代面板 fx
      }

      const mappings = mapEvent(ev);
      if (mappings.length === 0) continue;
      // 同一事件多个映射：按顺序触发，每个 setFx 独立 key（同时挂多玩家）
      for (const m of mappings) {
        const key = nextFxKey();
        setFx(m.playerId, { class: m.cls, key, dmg: m.dmg });
        if (m.sfx) playSfx(m.sfx);
        const pid = m.playerId;
        const dur = m.duration ?? FX_DURATION;
        setTimeout(() => clearFx(pid), dur);
      }
    }
  }, [state, setFx, clearFx, setJudgment, clearJudgment]);
}

// 无 UI 组件，仅挂载 watcher
export default function FxLayer(): null {
  useEventFx();
  return null;
}
